// DragonScales shader based on Abstract dragon scales
// Creates layered scale-like patterns with HSV color mapping and cell-based displacement
const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_scale;
uniform float u_depth;
uniform float u_brightness;
uniform float u_seed;

uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;
uniform vec3 u_color_3;
uniform vec3 u_color_4;
uniform vec3 u_color_5;

varying vec2 vUv;

#define PI 3.14159265359
#define TAU (2.0 * PI)

// HSV to RGB conversion
const vec4 hsv2rgb_K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
vec3 hsv2rgb(vec3 c) {
    vec3 p = abs(fract(c.xxx + hsv2rgb_K.xyz) * 6.0 - hsv2rgb_K.www);
    return c.z * mix(hsv2rgb_K.xxx, clamp(p - hsv2rgb_K.xxx, 0.0, 1.0), c.y);
}

// Simple hash function for procedural noise
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7)) + u_seed) * 43758.5453);
}

vec2 mod2_1(inout vec2 p) {
    p += 0.5;
    vec2 n = floor(p);
    p = fract(p) - 0.5;
    return n;
}

vec3 alphaBlend(vec3 back, vec4 front) {
    return mix(back, front.xyz, front.w);
}

// Egg-shaped distance function for scales
float egg(vec2 p, float ra, float rb) {
    const float k = sqrt(3.0);
    p.x = abs(p.x);
    float r = ra - rb;
    return ((p.y < 0.0) ? length(vec2(p.x, p.y)) - r :
            (k * (p.x + r) < p.y) ? length(vec2(p.x, p.y - k * r)) :
                                   length(vec2(p.x + r, p.y)) - 2.0 * r) - rb;
}

float cellhf(vec2 n) {
    float h = hash(0.005 * n + 0.5);
    h = sqrt(abs(h));
    return h;
}

float celldf(vec2 p, vec2 n) {
    float hh = cellhf(n);
    vec2 hxx = vec2(cellhf(n - vec2(1.0, 0.0)) - hh, cellhf(n - vec2(0.0, 1.0)) - hh);
    float hl = length(hxx);
    hxx /= max(hl, 1e-6);
    vec2 hyy = vec2(hxx.y, -hxx.x);
    const float off = 0.25;
    if (hl > 1e-6) {
        p = mat2(hxx.x, hxx.y, hyy.x, hyy.y) * p;
    }
    float r = (sqrt(0.5) + off);
    float d = egg(p.yx, r * 0.9, 0.25 * r);
    return d;
}

// Color lookup table using the 6 color palette
vec3 lut(float x) {
    if (x < 0.0 || x > 1.0) return u_color_0;

    float expand = 6.0;
    float P1 = abs(x - 0.0);
    float P2 = abs(x - 0.167);
    float P3 = abs(x - 0.334);
    float P4 = abs(x - 0.500);
    float P5 = abs(x - 0.667);
    float P6 = abs(x - 0.833);

    float R1 = smoothstep(1.0, -0.0, P1 * expand);
    float R2 = smoothstep(1.0, -0.0, P2 * expand);
    float R3 = smoothstep(1.0, -0.0, P3 * expand);
    float R4 = smoothstep(1.0, -0.0, P4 * expand);
    float R5 = smoothstep(1.0, -0.0, P5 * expand);
    float R6 = smoothstep(1.0, -0.0, P6 * expand);

    vec3 color = u_color_0 * R1 +
                 u_color_1 * R2 +
                 u_color_2 * R3 +
                 u_color_3 * R4 +
                 u_color_4 * R5 +
                 u_color_5 * R6;
    return color;
}

vec3 effect(vec2 p) {
    float sz = u_scale * 0.25;
    float amp = 10.0;
    float th = -TAU * sz * 20.0;
    
    // Add time-based movement
    p += amp * cos(vec2(1.0, sqrt(0.5)) * TAU * (u_time * u_speed) / (amp * 30.0));
    vec2 op = p;
    p /= sz;
    float aa = 2.0 / (sz * u_resolution.y);
    vec2 n = mod2_1(p);

    // Sample 9 cells (3x3 grid)
    vec3 col = vec3(0.0);
    for (int x = -1; x <= 1; x++) {
        for (int y = -1; y <= 1; y++) {
            vec2 off = vec2(float(x), float(y));
            vec2 pp = p - off;
            vec2 nn = n + off;
            
            float d = celldf(pp, nn);
            float h = cellhf(nn);
            
            // Use color palette instead of HSV
            float colorIndex = fract(0.05 * length(op) + h);
            vec4 bcol0 = vec4(lut(colorIndex), 1.0);
            vec4 bcol1 = vec4(bcol0.rgb * bcol0.rgb * 0.25, 0.5);
            vec4 bcol = mix(bcol0, bcol1, smoothstep(-th * aa, th * aa, sin(th * d)));
            
            float t = smoothstep(aa, -aa, d);
            vec4 ccol = bcol;
            ccol.w *= t;
            
            col *= mix(1.0, 0.25, exp(-u_depth * max(d, 0.0)));
            col = alphaBlend(col, ccol);
        }
    }

    return col * u_brightness;
}

void main() {
    vec2 p = (gl_FragCoord.xy - u_resolution.xy * 0.5) / u_resolution.y;
    vec3 col = effect(p);
    
    col = clamp(col, 0.0, 1.0);
    col = sqrt(col);
    gl_FragColor = vec4(col, 1.0);
}
`;

const shader = {
  name: 'DragonScales',
  shader: {
    frag,
  },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 2,
      max: 6,
      default: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd'],
    },
    { key: 'u_scale', type: 'number', label: 'Scale', min: 0.5, max: 5, step: 0.1, default: 1 },
    { key: 'u_depth', type: 'number', label: 'Depth', min: 1, max: 20, step: 0.5, default: 10 },
    {
      key: 'u_brightness',
      type: 'number',
      label: 'Brightness',
      min: 0.1,
      max: 3,
      step: 0.1,
      default: 1,
    },
    { key: 'speed', type: 'number', label: 'Speed', min: 0, max: 5, step: 0.1, default: 1 },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(DragonScales, colors=#ff6b6b,#4ecdc4,#45b7d1; u_scale=1; u_depth=10; animated=true)',
    'dynamic-shader(DragonScales, colors=#e17055,#81ecec,#fab1a0; u_scale=0.8; u_depth=15; animated=false)',
    'dynamic-shader(DragonScales, colors=#6c5ce7,#a29bfe,#fd79a8; u_scale=1.5; u_depth=8; animated=true)',
  ],
};

export default shader;
